<template>
  <div class="tasks-page">
    <div class="page-header mb-4">
      <div class="flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 class="text-900 mb-1">Задачи</h1>
          <p class="text-600 mb-0">Внутренний список задач</p>
        </div>
        <Button label="Добавить задачу" icon="pi pi-plus" @click="openCreate" />
      </div>
    </div>

    <div class="flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
      <SelectButton
        v-model="statusFilter"
        :options="statusOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        @change="loadTasks"
      />
      <span v-if="!loading" class="text-500 text-sm">{{ tasks.length }} задач(и)</span>
    </div>

    <!-- Загрузка -->
    <div v-if="loading" class="flex justify-content-center p-5">
      <ProgressSpinner style="width: 44px; height: 44px" strokeWidth="4" />
    </div>

    <!-- Пусто -->
    <div v-else-if="!tasks.length" class="empty-state">
      <i class="pi pi-check-circle"></i>
      <p>{{ emptyText }}</p>
      <Button
        v-if="statusFilter === 'active'"
        label="Создать первую задачу"
        icon="pi pi-plus"
        variant="text"
        @click="openCreate"
      />
    </div>

    <!-- Сетка плиток -->
    <div v-else class="tasks-grid">
      <TaskCard
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        @toggle="onToggle"
        @open="openDetail"
      />
    </div>

    <TaskDetailDialog
      v-model:visible="detailVisible"
      :task="selectedTask"
      @toggle="onToggle"
      @edit="onEditFromDetail"
      @delete="onDelete"
    />

    <TaskFormDialog
      v-model:visible="formVisible"
      :task="editingTask"
      :users="users"
      @saved="loadTasks"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Button from 'primevue/button'
import SelectButton from 'primevue/selectbutton'
import ProgressSpinner from 'primevue/progressspinner'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import apiService from '../services/apiService'
import TaskCard from '../components/tasks/TaskCard.vue'
import TaskDetailDialog from '../components/tasks/TaskDetailDialog.vue'
import TaskFormDialog from '../components/tasks/TaskFormDialog.vue'
import { STATUS_FILTER_OPTIONS } from '../utils/taskConstants'

const toast = useToast()
const confirm = useConfirm()

const tasks = ref([])
const users = ref([])
const loading = ref(false)
const statusFilter = ref('active')
const statusOptions = STATUS_FILTER_OPTIONS

const formVisible = ref(false)
const editingTask = ref(null)
const detailVisible = ref(false)
const selectedTask = ref(null)

const emptyText = computed(() => {
  if (statusFilter.value === 'done') return 'Выполненных задач пока нет'
  if (statusFilter.value === 'all') return 'Задач пока нет'
  return 'Активных задач нет — всё сделано 🎉'
})

async function loadTasks() {
  loading.value = true
  try {
    tasks.value = await apiService.getTasks(statusFilter.value)
    // Если открыта модалка деталей — обновим её данными из свежего списка
    if (selectedTask.value) {
      const fresh = tasks.value.find((t) => t.id === selectedTask.value.id)
      if (fresh) selectedTask.value = fresh
    }
  } catch (err) {
    console.error(err)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить задачи', life: 3000 })
  } finally {
    loading.value = false
  }
}

async function loadUsers() {
  try {
    users.value = await apiService.getUsers()
  } catch (err) {
    console.error(err)
  }
}

function openCreate() {
  editingTask.value = null
  formVisible.value = true
}

function openDetail(task) {
  selectedTask.value = task
  detailVisible.value = true
}

function onEditFromDetail(task) {
  editingTask.value = task
  detailVisible.value = false
  formVisible.value = true
}

async function onToggle(task) {
  try {
    const updated = await apiService.toggleTask(task.id)
    if (selectedTask.value && selectedTask.value.id === task.id) {
      selectedTask.value = updated
    }
    if (statusFilter.value === 'all') {
      const idx = tasks.value.findIndex((t) => t.id === task.id)
      if (idx !== -1) tasks.value[idx] = updated
    } else {
      // статус сменился — задача больше не подходит под текущий фильтр
      tasks.value = tasks.value.filter((t) => t.id !== task.id)
    }
  } catch (err) {
    console.error(err)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось изменить статус', life: 3000 })
  }
}

function onDelete(task) {
  confirm.require({
    message: `Удалить задачу «${task.title}»?`,
    header: 'Подтверждение',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await apiService.deleteTask(task.id)
        tasks.value = tasks.value.filter((t) => t.id !== task.id)
        detailVisible.value = false
        toast.add({ severity: 'success', summary: 'Удалено', detail: 'Задача удалена', life: 2500 })
      } catch (err) {
        console.error(err)
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить задачу', life: 3000 })
      }
    }
  })
}

onMounted(() => {
  loadTasks()
  loadUsers()
})
</script>

<style scoped>
.tasks-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem;
}

.tasks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  align-items: stretch;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3.5rem 1rem;
  color: var(--text-color-secondary);
  text-align: center;
}

.empty-state i {
  font-size: 2.5rem;
  color: var(--primary-color);
  opacity: 0.6;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}
</style>
